import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../redux/reducers";
import { sessionEnd } from "../../redux/actions";
import {
  Box, CssBaseline, Card, Table, TableHead, TableBody, TableRow, TableCell,
  TextField, IconButton, Typography, Alert, Tooltip,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import RestoreIcon from "@mui/icons-material/Restore";
import NavTab from "../../layout/NavTab";

import { RegieBaremeKey, RegieBaremeScalars, IRegieBaremeIK, IRegieBaremeHistoryEntry, RegieVehicleType } from "../../interface/regie";
import { getRegieBaremes } from "../../tools/regie/getRegieBaremes";
import { getRegieBaremeHistory } from "../../tools/regie/getRegieBaremeHistory";
import { setRegieBareme } from "../../tools/regie/setRegieBareme";
import { setRegieBaremeIK } from "../../tools/regie/setRegieBaremeIK";

const SCALAR_LABELS: Record<RegieBaremeKey, string> = {
  chargesPatronalesRate: "Charges patronales (ratio, 0.42 = 42 %)",
  primeAnnuelleCongesRate: "Prime annuelle de congés (ratio, 0.42 = 42 %)",
  joursAnnee: "Nb jours dans l'année",
  joursChomesParAn: "Nb jours chômés / an",
  titresRestaurantPartEmployeur: "Titres Restaurant — part employeur (€)",
  titresRestaurantPartEmployeurRatio: "Titres Restaurant — ratio part employeur (0.60 = 60 %)",
  mutuellePartPatronaleRate: "Mutuelle — part patronale (ratio, 0.42 = 42 %)",
  plafondAnnuelSecu: "Plafond annuel Sécu (€)",
  poleSanteTravail: "Pôle Santé Travail (€ / an)",
  formationContinueTaxeApprentissage: "Formation continue / Taxe d'apprentissage (€ / an)",
  bsSalarieMensuel: "BS Salarié (€ / mois)",
  indemniteKmForfaitaireMensuelle: "Indemnité km forfaitaire (€ / mois)",
  margeCibleDefaut: "Marge cible par défaut (0.40 = 40 %)",
};

const SCALAR_KEYS = Object.keys(SCALAR_LABELS) as RegieBaremeKey[];

const VEHICLE_GROUPS: { vehicleType: RegieVehicleType; label: string }[] = [
  { vehicleType: "voiture", label: "Voiture" },
  { vehicleType: "voiture_electrique", label: "Voiture électrique" },
  { vehicleType: "moto", label: "Moto" },
];

export default function RegieBaremesAdmin() {
  const dispatch = useDispatch();
  const token = useSelector((state: RootState) => state.user.token);

  const [scalars, setScalars] = useState<RegieBaremeScalars | null>(null);
  const [ik, setIK] = useState<IRegieBaremeIK[]>([]);
  const [history, setHistory] = useState<IRegieBaremeHistoryEntry[]>([]);
  const [editedScalars, setEditedScalars] = useState<Partial<Record<RegieBaremeKey, string>>>({});
  const [editedIK, setEditedIK] = useState<Record<string, { perKmRate?: string; fixedAmount?: string }>>({});
  const [error, setError] = useState("");

  const load = () => {
    if (!token) return;
    getRegieBaremes(token, (baremes) => { setScalars(baremes.scalars); setIK(baremes.ik); }, dispatch, setError);
    getRegieBaremeHistory(token, setHistory, dispatch, setError);
  };

  useEffect(load, [token]);

  const handleSaveScalar = (key: RegieBaremeKey) => {
    if (!token) return;
    const raw = editedScalars[key];
    if (raw === undefined || raw === "") return;
    setRegieBareme(key, Number(raw), token, (updated) => {
      setScalars(updated);
      setEditedScalars((previous) => ({ ...previous, [key]: undefined }));
      getRegieBaremeHistory(token, setHistory, dispatch, setError);
    }, dispatch, setError);
  };

  const ikRowKey = (bracket: IRegieBaremeIK) => `${bracket.vehicleType}|${bracket.fiscalPowerBracket}|${bracket.kmRangeMin}`;

  const handleSaveIK = (bracket: IRegieBaremeIK) => {
    if (!token) return;
    const edited = editedIK[ikRowKey(bracket)];
    if (!edited) return;
    const updatedBracket: IRegieBaremeIK = {
      ...bracket,
      perKmRate: edited.perKmRate !== undefined && edited.perKmRate !== "" ? Number(edited.perKmRate) : bracket.perKmRate,
      fixedAmount: edited.fixedAmount !== undefined && edited.fixedAmount !== "" ? Number(edited.fixedAmount) : bracket.fixedAmount,
    };
    setRegieBaremeIK(updatedBracket, token, setIK, dispatch, setError);
    setEditedIK((previous) => ({ ...previous, [ikRowKey(bracket)]: {} }));
  };

  const renderVehicleMatrix = (vehicleType: RegieVehicleType, label: string) => {
    const brackets = ik.filter((bracket) => bracket.vehicleType === vehicleType);
    if (brackets.length === 0) return null;

    const fiscalPowers = Array.from(new Set(brackets.map((bracket) => bracket.fiscalPowerBracket)));
    const kmColumns = Array.from(new Map(brackets.map((bracket) => [bracket.kmRangeMin, bracket])).values())
      .sort((a, b) => a.kmRangeMin - b.kmRangeMin);

    return (
      <Box key={vehicleType} sx={{ mb: 3 }}>
        <Typography variant="subtitle1" sx={{ mb: 1 }}>{label}</Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell rowSpan={2}>Puissance fiscale</TableCell>
              {kmColumns.map((column) => (
                <TableCell key={column.kmRangeMin} colSpan={3} align="center">{column.kmBracketLabel}</TableCell>
              ))}
            </TableRow>
            <TableRow>
              {kmColumns.map((column) => (
                <React.Fragment key={column.kmRangeMin}>
                  <TableCell align="center">€ / km</TableCell>
                  <TableCell align="center">Forfait €</TableCell>
                  <TableCell></TableCell>
                </React.Fragment>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {fiscalPowers.map((fiscalPowerBracket) => {
              const rowLabel = brackets.find((bracket) => bracket.fiscalPowerBracket === fiscalPowerBracket);
              return (
                <TableRow key={fiscalPowerBracket}>
                  <TableCell>{rowLabel?.fiscalPowerBracketLabel}</TableCell>
                  {kmColumns.map((column) => {
                    const bracket = brackets.find(
                      (candidate) => candidate.fiscalPowerBracket === fiscalPowerBracket && candidate.kmRangeMin === column.kmRangeMin
                    );
                    if (!bracket) return <TableCell key={column.kmRangeMin} colSpan={3}>—</TableCell>;
                    const rowKey = ikRowKey(bracket);
                    const edited = editedIK[rowKey] || {};
                    return (
                      <React.Fragment key={column.kmRangeMin}>
                        <TableCell>
                          <TextField
                            type="number" variant="standard"
                            placeholder={String(bracket.perKmRate)}
                            value={edited.perKmRate ?? ""}
                            onChange={(e) => setEditedIK((previous) => ({ ...previous, [rowKey]: { ...previous[rowKey], perKmRate: e.target.value } }))}
                            sx={{ width: 70 }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            type="number" variant="standard"
                            placeholder={String(bracket.fixedAmount)}
                            value={edited.fixedAmount ?? ""}
                            onChange={(e) => setEditedIK((previous) => ({ ...previous, [rowKey]: { ...previous[rowKey], fixedAmount: e.target.value } }))}
                            sx={{ width: 70 }}
                          />
                        </TableCell>
                        <TableCell>
                          <IconButton size="small" disabled={!edited.perKmRate && !edited.fixedAmount} onClick={() => handleSaveIK(bracket)}>
                            <SaveIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </React.Fragment>
                    );
                  })}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>
    );
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f8fafc" }}>
      <CssBaseline />
      <NavTab text="Barèmes régie" displayReturn />
      <Card sx={{ m: 3, p: 3, borderRadius: "16px", border: "1px solid #e2e8f0" }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Typography variant="h6" sx={{ mb: 2 }}>Taux et montants</Typography>
        <Table size="small" sx={{ mb: 4 }}>
          <TableHead>
            <TableRow>
              <TableCell>Paramètre</TableCell>
              <TableCell>Valeur actuelle</TableCell>
              <TableCell>Nouvelle valeur</TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {SCALAR_KEYS.map((key) => (
              <TableRow key={key}>
                <TableCell>{SCALAR_LABELS[key]}</TableCell>
                <TableCell>{scalars ? scalars[key] : "…"}</TableCell>
                <TableCell>
                  <TextField
                    type="number"
                    variant="standard"
                    placeholder={scalars ? String(scalars[key]) : ""}
                    value={editedScalars[key] ?? ""}
                    onChange={(e) => setEditedScalars((previous) => ({ ...previous, [key]: e.target.value }))}
                    sx={{ width: 120 }}
                  />
                </TableCell>
                <TableCell>
                  <IconButton size="small" disabled={!editedScalars[key]} onClick={() => handleSaveScalar(key)}>
                    <SaveIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <Typography variant="h6" sx={{ mb: 2 }}>Barème kilométrique</Typography>
        <Box sx={{ mb: 2 }}>
          {VEHICLE_GROUPS.map((group) => renderVehicleMatrix(group.vehicleType, group.label))}
        </Box>

        <Typography variant="h6" sx={{ mb: 2 }}>Historique des taux et montants</Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Paramètre</TableCell>
              <TableCell>Ancienne valeur</TableCell>
              <TableCell>Nouvelle valeur</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Modifié par</TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {history.length === 0 && (
              <TableRow>
                <TableCell colSpan={6}>
                  <Typography variant="body2" color="text.secondary">Aucune modification enregistrée (valeurs par défaut).</Typography>
                </TableCell>
              </TableRow>
            )}
            {history.map((entry) => (
              <TableRow key={entry._id}>
                <TableCell>{SCALAR_LABELS[entry.key]}</TableCell>
                <TableCell>{entry.previousValue}</TableCell>
                <TableCell>{entry.value}</TableCell>
                <TableCell>{new Date(entry.createdAt).toLocaleString("fr-FR")}</TableCell>
                <TableCell>{entry.createdBy?.email || "—"}</TableCell>
                <TableCell>
                  <Tooltip title="Réutiliser l'ancienne valeur">
                    <IconButton
                      size="small"
                      onClick={() => setEditedScalars((previous) => ({ ...previous, [entry.key]: String(entry.previousValue) }))}
                    >
                      <RestoreIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </Box>
  );
}
